import React, { useState, useMemo, useEffect } from 'react';
import { ChevronDown, Search, Check } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

/**
 * MultiSelectPopover - A multi-select dropdown styled like AssetSelectorBeta
 *
 * @param {Array} value - Array of selected values
 * @param {Function} onChange - Callback when selection changes (receives array of values)
 * @param {Array} options - Array of option objects: [{ value: 'id', label: 'Name' }]
 * @param {String} placeholder - Placeholder text when nothing selected
 * @param {String} className - Additional CSS classes
 * @param {Boolean} disablePortal - Disable portal for nested menus
 */
const MultiSelectPopover = ({
	value = [],
	onChange,
	options = [],
	placeholder = 'Select...',
	className = '',
	disablePortal = false,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');

	const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

	// Clear search when closing
	useEffect(() => {
		if (!isOpen) {
			setSearchQuery('');
		}
	}, [isOpen]);

	// Filter options based on search query
	const filteredOptions = useMemo(() => {
		if (!searchQuery.trim()) return options;

		const query = searchQuery.toLowerCase();
		return options.filter((option) =>
			option.label?.toLowerCase().includes(query)
		);
	}, [options, searchQuery]);

	const handleToggle = (optionValue) => {
		const newValue = selectedValues.includes(optionValue)
			? selectedValues.filter((v) => v !== optionValue)
			: [...selectedValues, optionValue];
		onChange(newValue);
	};

	const handleClear = () => {
		onChange([]);
	};

	// Get display text for trigger button
	const getDisplayText = () => {
		if (selectedValues.length === 0) {
			return <span className="text-gray-400">{placeholder}</span>;
		}
		if (selectedValues.length === 1) {
			const option = options.find((o) => o.value === selectedValues[0]);
			return (
				<span className="text-gray-900">
					{option?.label || selectedValues[0]}
				</span>
			);
		}
		return (
			<span className="text-gray-900">
				{selectedValues.length} selected
			</span>
		);
	};

	return (
		<div className={className}>
			<Popover open={isOpen} onOpenChange={setIsOpen} modal={false}>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[40px]"
					>
						<span className="flex items-center gap-2 truncate flex-1 min-w-0">
							{getDisplayText()}
						</span>
						<ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
					</button>
				</PopoverTrigger>
				<PopoverContent
					className="w-[var(--radix-popover-trigger-width)] p-0 shadow-lg ring-1 ring-black ring-opacity-5 bg-white"
					align="start"
					onClick={(e) => e.stopPropagation()}
					disablePortal={disablePortal}
				>
					{/* Search Input */}
					<div className="p-3 border-b border-gray-200 bg-white">
						<div className="relative">
							<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
							<input
								type="text"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search..."
								className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
							/>
						</div>
					</div>

					{/* Options List */}
					<div className="h-[240px] overflow-y-auto">
						<div className="p-2">
							{filteredOptions.length === 0 ? (
								<div className="flex flex-col items-center justify-center h-full text-gray-500 py-8">
									<p className="text-sm">No options found</p>
									{searchQuery && (
										<p className="text-xs mt-1">Try a different search term</p>
									)}
								</div>
							) : (
								filteredOptions.map((option) => {
									const isSelected = selectedValues.includes(option.value);
									return (
										<div
											key={option.value}
											className={`flex items-center gap-2 px-2 py-2 rounded-sm hover:bg-gray-100 cursor-pointer ${
												isSelected ? 'bg-indigo-50' : ''
											}`}
											onClick={() => handleToggle(option.value)}
										>
											<input
												type="checkbox"
												checked={isSelected}
												onChange={() => handleToggle(option.value)}
												className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
												onClick={(e) => e.stopPropagation()}
											/>
											<span className="text-sm text-gray-900 flex-1">
												{option.label}
											</span>
											{isSelected && (
												<Check className="h-4 w-4 text-indigo-600" />
											)}
										</div>
									);
								})
							)}
						</div>
					</div>

					{/* Footer */}
					{selectedValues.length > 0 && (
						<div className="flex items-center justify-between border-t px-3 py-2 bg-gray-50">
							<span className="text-xs text-gray-500">
								{selectedValues.length} selected
							</span>
							<button
								type="button"
								onClick={handleClear}
								className="text-xs font-medium text-indigo-600 hover:text-indigo-700"
							>
								Clear
							</button>
						</div>
					)}
				</PopoverContent>
			</Popover>
		</div>
	);
};

export default MultiSelectPopover;
